import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  PaidMediaOverviewRevenueContributionLeader,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewIndex,
  formatOverviewPct,
  OVERVIEW_EMPTY_VALUE,
} from "../../utils/PaidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { overviewPlatformLabel } from "../../utils/PaidMediaOverviewLabels";
import { OverviewChartEmptyState, OverviewMetricTip } from "./OverviewPrimitives";
import { OVERVIEW_GRID_COLOR, overviewAxisProps } from "./OverviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

const formatSharePercent = (value: number | null) =>
  value === null || !Number.isFinite(value) ? OVERVIEW_EMPTY_VALUE : formatOverviewPct(value, 0);

interface InvestmentEfficiencyCardProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  revenueContributionLeader: PaidMediaOverviewRevenueContributionLeader | null;
}

const InvestmentEfficiencyCard: FC<InvestmentEfficiencyCardProps> = ({
  platforms,
  revenueContributionLeader,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const meta = platforms?.find(entry => entry?.platform === "meta_ads");
  const google = platforms?.find(entry => entry?.platform === "google_ads");

  const chartData = [
    {
      platform: t("paidMediaOverview.platforms.meta"),
      spend: meta?.spend_share ?? null,
      revenue: meta?.revenue_share ?? null,
    },
    {
      platform: t("paidMediaOverview.platforms.google"),
      spend: google?.spend_share ?? null,
      revenue: google?.revenue_share ?? null,
    },
  ];

  const hasShares = chartData.some(row => row.spend !== null || row.revenue !== null);
  const leaderLabel = overviewPlatformLabel(t, revenueContributionLeader?.platform);

  return (
    <article className="paid-media-panel">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.investment.title")}
            <OverviewMetricTip
              id="spendEfficiencyIndex"
              label={t("paidMediaOverview.investment.title")}
            />
          </h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.investment.subtitle")}
          </p>
        </div>
      </div>
      <div className="paid-media-overview__investment-grid">
        <div className="paid-media-overview__chart paid-media-overview__chart--md paid-media-overview__investment-chart">
          {hasShares ? (
            <ResponsiveContainer>
              <BarChart
                layout="vertical"
                data={chartData}
                margin={chartRtl.margin({ top: 5, right: 20, bottom: 5, left: 10 })}
              >
                <CartesianGrid stroke={OVERVIEW_GRID_COLOR} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={value => formatOverviewPct(Number(value), 0)}
                  domain={[0, 100]}
                  reversed={chartRtl.axisReversed}
                  {...overviewAxisProps}
                />
                <YAxis
                  type="category"
                  dataKey="platform"
                  width={90}
                  orientation={chartRtl.yAxisOrientation}
                  {...overviewAxisProps}
                />
                <Tooltip
                  wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatSharePercent(value)}
                    />
                  }
                />

                <Bar
                  dataKey="spend"
                  name={t("paidMediaOverview.investment.seriesSpendShare")}
                  className="paid-media-overview__share-bar paid-media-overview__share-bar--spend"
                  fill="#2b74e4"
                  radius={[3, 3, 3, 3]}
                >
                  <LabelList
                    dataKey="spend"
                    position={chartRtl.labelPosition}
                    formatter={(value: number) => formatSharePercent(value)}
                    className="paid-media-overview__share-bar-label"
                  />
                </Bar>
                <Bar
                  dataKey="revenue"
                  name={t("paidMediaOverview.investment.seriesRevenueShare")}
                  className="paid-media-overview__share-bar paid-media-overview__share-bar--revenue"
                  fill="#8d4fd3"
                  radius={[3, 3, 3, 3]}
                >
                  <LabelList
                    dataKey="revenue"
                    position={chartRtl.labelPosition}
                    formatter={(value: number) => formatSharePercent(value)}
                    className="paid-media-overview__share-bar-label"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <OverviewChartEmptyState />
          )}
        </div>
        <div className="paid-media-overview__index-panel">
          <div className="paid-media-overview__index-panel-title">
            {t("paidMediaOverview.investment.indexTitle")}
            <OverviewMetricTip
              id="spendEfficiencyIndex"
              label={t("paidMediaOverview.investment.indexTitle")}
            />
          </div>
          <div className="paid-media-overview__index-panel-subtitle">
            {t("paidMediaOverview.investment.indexSubtitle")}
          </div>
          <div className="paid-media-overview__index-rows">
            <IndexRow
              label={t("paidMediaOverview.platforms.meta")}
              value={meta?.efficiency_index ?? null}
              formattedValue={meta?.formatted_efficiency_index ?? null}
              platformKey="meta"
            />
            <IndexRow
              label={t("paidMediaOverview.platforms.google")}
              value={google?.efficiency_index ?? null}
              formattedValue={google?.formatted_efficiency_index ?? null}
              platformKey="google"
            />
          </div>
          <div className="paid-media-overview__index-panel-footnote">
            <div>
              <strong className="paid-media-overview__text-success">
                {t("paidMediaOverview.investment.aboveLabel")}
              </strong>{" "}
              {t("paidMediaOverview.investment.aboveCopy")}
            </div>
            <div>
              <strong className="paid-media-overview__text-danger">
                {t("paidMediaOverview.investment.belowLabel")}
              </strong>{" "}
              {t("paidMediaOverview.investment.belowCopy")}
            </div>
          </div>
        </div>
      </div>
      {leaderLabel ? (
        <div className="paid-media-overview__callout paid-media-overview__callout--success">
          <strong>{t("paidMediaOverview.investment.conclusionLabel")}</strong>{" "}
          {t("paidMediaOverview.investment.conclusionCopy", {
            platform: leaderLabel,
            index: formatOverviewIndex(revenueContributionLeader?.efficiency_index),
          })}
        </div>
      ) : null}
    </article>
  );
};

const IndexRow: FC<{
  label: string;
  value: number | null;
  formattedValue?: string | null;
  platformKey: "meta" | "google";
}> = ({ label, value, formattedValue, platformKey }) => {
  const hasValue = value !== null && Number.isFinite(value);
  const good = hasValue && (value as number) >= 1;
  const toneClass = !hasValue
    ? ""
    : good
      ? "paid-media-overview__text-success"
      : "paid-media-overview__text-danger";

  return (
    <div className="paid-media-overview__index-row">
      <div className="paid-media-overview__index-row-label">
        <span
          className={`paid-media-overview__index-row-dot paid-media-overview__index-row-dot--${platformKey}`}
          aria-hidden
        />
        {label}
      </div>
      <span className={`paid-media-overview__index-row-value ${toneClass}`.trim()}>
        {formattedValue ?? formatOverviewIndex(value)}
      </span>
    </div>
  );
};

export default InvestmentEfficiencyCard;
